/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AbstractFormComponent, BuilderNavigationService, FormFlowState } from '@gh/builder-shared';
import { BuilderPermission, SalesFolderStatus } from '@gh/builder-shared-data';
import { TimerEvent } from '@gh/common-internal-shared';
import {
  ActionFlow,
  DEFAULT_AUTOSAVE_INTERVALS,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  getRouteParentTitle,
  ImmutablePageState,
  isRouteForCreationMode,
  PAGE_STATE,
  queryAll,
  queryDirty,
  queryFormMode,
  queryRecalculationNeeded,
  State,
  UserContext,
} from '@gh/common-shared';
import { TranslationTupleOrId } from '@gh/core-mls';
import { FormMode } from '@gh/core-ui';
import { getLocalValue, setLocalValue } from '@gh/core-util';
import { map$, skip$ } from '@gh/rx/operators';

import { isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';

import { QuoteDetailsDerivedInfo, QuoteDetailsMasterInfo } from '../../resources/quote.model';
import { QuoteDetailsCommandService } from './quote-details-command.service';
import { quoteDetailsReducer } from './quote-details.reducers';
import {
  queryQuoteDerivedInfo,
  queryQuoteId,
  queryQuoteMasterInfo,
  queryQuoteMasterInfoShadow,
  queryQuoteSettings,
  QUOTE_DETAILS_INIT_STATE,
  QuoteDetailsSettings,
  QuoteDetailsState,
} from './quote-details.state';

const QUOTE_AUTOSAVE_LOCAL_KEY = 'quote:autosave';

export function quoteDetailsStateFactory(): State<QuoteDetailsState> {
  return new State(QUOTE_DETAILS_INIT_STATE, quoteDetailsReducer);
}

export function quoteDetailsFormFactory(state: State<QuoteDetailsState>): ImmutablePageState<QuoteDetailsState> {
  return new ImmutablePageState(state, {
    formMode: queryFormMode,
    dirty: queryDirty,
  });
}

function getQuoteTitle(mode: FormMode): string {
  return {
    [FormMode.CreateMode]: 'lbl_quote_create',
    [FormMode.EditMode]: 'lbl_quote_edit',
    [FormMode.ViewMode]: 'lbl_quote_view',
  }[mode];
}

@Component({
  standalone: false,
  selector: 'gh-quote-details',
  templateUrl: 'quote-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: State, useFactory: quoteDetailsStateFactory },
    { provide: PAGE_STATE, useFactory: quoteDetailsFormFactory, deps: [State] },
    QuoteDetailsCommandService,
  ],
})
export class QuoteDetailsComponent extends AbstractFormComponent<QuoteDetailsState> implements OnInit, OnDestroy {
  recalculationNeeded$: Observable<boolean>;
  masterInfo$: Observable<QuoteDetailsMasterInfo>;
  derivedInfo$: Observable<QuoteDetailsDerivedInfo>;
  masterInfoShadowSnapshot$: Observable<FormStateSnapshot<QuoteDetailsMasterInfo>>;
  description$: Observable<string>;
  rfqNumber$: Observable<Maybe<number>>;
  jobNumber$: Observable<Maybe<number>>;

  formModeTitle$ = this.form.mode$.pipe(map$(getQuoteTitle));

  autosaveIntervals = DEFAULT_AUTOSAVE_INTERVALS;
  currentAutosaveInterval = getLocalValue(QUOTE_AUTOSAVE_LOCAL_KEY, '30m');
  autoSaveActivityTick = 0;
  private autoSaveActivitySubscription?: Subscription;

  constructor(private route: ActivatedRoute,
              @Inject(PAGE_STATE) public form: ImmutablePageState<QuoteDetailsState>,
              private state: State<QuoteDetailsState>,
              private navigationService: BuilderNavigationService,
              private commands: QuoteDetailsCommandService,
              private userContext: UserContext,
              private changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  get customerName(): string {
    return this.state.project(queryQuoteDerivedInfo).customerName;
  }

  get salesFolderName(): string {
    return this.state.project(queryQuoteDerivedInfo).salesFolderName;
  }

  get completeFlow(): ActionFlow<FormFlowState<QuoteDetailsState>> {
    return this.commands.completeFlow();
  }

  get code(): number {
    return this.state.project(queryQuoteDerivedInfo).code;
  }

  get createdDate(): Date {
    return this.state.project(queryQuoteDerivedInfo).created;
  }

  get settings(): QuoteDetailsSettings {
    return this.state.project(queryQuoteSettings);
  }

  get isCompleted(): boolean {
    const { salesFolderStatus, isCompletionQuote } = this.state.project(queryQuoteDerivedInfo);
    return salesFolderStatus === SalesFolderStatus.Completed && isCompletionQuote;
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_quotations_list';
  }

  ngOnInit(): void {
    const { init } = getRouteInitData(this.route);

    const mode = isRouteForCreationMode(this.route)
      ? FormMode.CreateMode : (this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT)
        && isNil(init.derivedInfo.rfqNumber) && SalesFolderStatus.Active === init.derivedInfo.salesFolderStatus)
        ? FormMode.EditMode : FormMode.ViewMode;

    this.recalculationNeeded$ = this.state.select(queryRecalculationNeeded);
    this.masterInfo$ = this.state.select(queryQuoteMasterInfo).pipe(map$(getFormStateSnapshotValue));
    this.masterInfoShadowSnapshot$ = this.state.select(queryQuoteMasterInfoShadow);
    this.description$ = this.masterInfo$.pipe(map$((masterInfo) => masterInfo.description));
    this.derivedInfo$ = this.state.select(queryQuoteDerivedInfo);
    this.rfqNumber$ = this.derivedInfo$.pipe(map$((derived) => derived.rfqNumber));
    this.jobNumber$ = this.derivedInfo$.pipe(map$((derived) => derived.jobNumber));

    this.commands.load(init, mode);
    this.autoSaveActivitySubscription = this.state.pipe(skip$(1)).subscribe(() => {
      if (this.state.project(queryDirty)) {
        this.autoSaveActivityTick++;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.autoSaveActivitySubscription?.unsubscribe();
  }

  onRecalculate(): void {
    const flow = this.commands.recalculateFlow();

    flow.run({
      form: this.form,
      value: this.state.project(queryAll),
    });
  }

  onPrint(): void {
    this.commands.print();
  }

  onAutoSave(event: TimerEvent): void {
    event.waitFor(this.commands.autoSave(this.form));
  }

  onSearchHousePlan(): void {
    this.commands.searchHousePlan();
  }

  onOpenGto(value: any): void {
    this.commands.openGto(value.isDisabledGto);
  }

  onOpenHpe(): void {
    this.commands.openHpe();
  }

  onOpenStr(): void {
    this.commands.openStr();
  }

  onOpenTops(value: any): void {
    this.commands.openTops(value.formMode);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.state.project(queryQuoteId),
      entityType: 'QUOTATION',
      label: getQuoteTitle(this.form.mode),
    });
  }

  onChangeAutosaveInterval(interval: string): void {
    setLocalValue(QUOTE_AUTOSAVE_LOCAL_KEY, interval);
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<QuoteDetailsMasterInfo>): void {
    this.commands.setMasterInfo(masterInfo);
  }
}
